import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, TextField, CircularProgress, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, CardContent, Divider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import { colors } from '../../colors';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import Notification from '../Notification';

const TemplateManagement = () => {
    const [file, setFile] = useState(null);
    const [templateName, setTemplateName] = useState('');  // New state for template name
    const [message, setMessage] = useState('');
    const [templates, setTemplates] = useState([]);
    const [hoveredTemplate, setHoveredTemplate] = useState(null);
    const [open, setOpen] = useState(false);
    const [createSurveyOpen, setCreateSurveyOpen] = useState(false);
    const [surveyData, setSurveyData] = useState({
        surveyName: '',
        surveyDescription: '',
        universityName: '',
        logo: null,
        uniqueLinksCount: '',
        validTill: null,
        templateId: ''
    });

    const [selectedTemplate, setSelectedTemplate] = useState(null); // To track selected template
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // To control delete confirmation modal
    const [isAddingTemplate, setIsAddingTemplate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);


    const navigate = useNavigate();

    const fetchTemplates = async () => {
        try {
            const response = await axiosInstance.get('/api/v2/templates');
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch the existing templates from the backend
    useEffect(() => {
        fetchTemplates();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress size={50} />
            </Box>
        );
    }

    // Handle file selection for upload
    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    // Handle form submission for template upload
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file || !templateName) {  // Check for file and template name
            setMessage('Please provide both a template name and file.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('templateName', templateName);  // Add template name to formData

        try {
            // Upload template file
            const response = await axiosInstance.post('/api/v2/templates/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Success: Show success message and reset form
            setMessage(response.data);
            setTemplateName('');        // Clear template name input
            setFile(null);              // Clear file input field
            setNotification({ message: 'File uploaded successfully!', type: 'success' });

            // Delay 2 seconds before performing other actions
            setTimeout(() => {
                setNotification(null);  // Clear notification
                fetchTemplates();       // Refresh templates list
                setIsAddingTemplate(false);  // Close the add template form
            }, 2000);
        } catch (error) {
            console.error('Error uploading file:', error);

            // Extract error message from the response if available
            const errorMessage = error.response?.data?.message || 'Failed to upload the file.';
            setMessage(errorMessage);

            // Display error notification
            setNotification({
                message: 'Failed to upload template. Please try again.',
                type: 'error',
            });

            // Auto-clear notification after 4 seconds
            setTimeout(() => setNotification(null), 4000);
        }

    };

    // Handle template name change
    const handleTemplateNameChange = (e) => {
        setTemplateName(e.target.value);
    };

    // Handle opening the dialog with the template details
    const handleTemplateClick = (template) => {
        navigate(`/admin/temp-view/${template.id}`);
    };

    // Handle closing the template details dialog
    const handleClose = () => {
        setOpen(false);
    };

    // Handle opening the Create Survey form and setting templateId
    const handleCreateSurveyClick = (templateId) => {
        setSurveyData((prevData) => ({
            ...prevData,
            templateId: templateId,  // Set the templateId based on the selected template
        }));
        setCreateSurveyOpen(true);  // Open the Create Survey dialog
    };

    // Handle survey form data change
    const handleSurveyDataChange = (e) => {
        const { name, value } = e.target;
        setSurveyData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Handle file change for logo
    const handleLogoChange = (e) => {
        setSurveyData((prevData) => ({
            ...prevData,
            logo: e.target.files[0],
        }));
    };

    // Handle the valid till date change
    const handleValidTillChange = (newDate) => {
        setSurveyData((prevData) => ({
            ...prevData,
            validTill: newDate,
        }));
    };

    // Handle closing the Create Survey form
    const handleCreateSurveyClose = () => {
        setCreateSurveyOpen(false);
    };

    // Handle API call for creating survey
    const handleCreateSurvey = async () => {
        const formData = new FormData();
        formData.append('surveyName', surveyData.surveyName);
        formData.append('surveyDescription', surveyData.surveyDescription);
        formData.append('universityName', surveyData.universityName);
        formData.append('logo', surveyData.logo);
        formData.append('uniqueLinksCount', surveyData.uniqueLinksCount);  // Corrected field
        formData.append('validTill', surveyData.validTill);
        formData.append('templateId', surveyData.templateId);

        try {
            const response = await axiosInstance.post('/api/surveys', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200) {
                setNotification({ message: 'Survey Created Successfully!', type: 'success' });
                setTimeout(() => {
                    setNotification(null);
                    navigate('/admin/dashboard', {
                        state: { view: 'ViewSurvey' },
                    });
                }, 2000);
                // navigate('/admin/dashboard'); // Navigate to dashboard after success
            }
        } catch (error) {
            console.error("Error Saving Template:", error);
            setNotification({
                message: 'Failed to create Survey. Please try again.',
                type: 'error',
            });
            setTimeout(() => setNotification(null), 4000);
        }
    };

    // Handle delete template button click
    const handleDeleteTemplate = (template) => {
        setSelectedTemplate(template);
        setDeleteConfirmOpen(true); // Open confirmation dialog
    };

    // Confirm delete template
    const confirmDeleteTemplate = async () => {
        try {
            await axiosInstance.delete(`/api/v2/templates/${selectedTemplate.id}`);
            setDeleteConfirmOpen(false); // Close confirmation modal
            setNotification({ message: 'Template Deleted successfully!', type: 'success' });
            setTimeout(() => {
                setNotification(null);
                fetchTemplates();
            }, 2000);
            //fetchTemplates(); // Refetch templates after deletion
            //setMessage('Template deleted successfully.');
        } catch (error) {
            console.error('Error deleting template:', error);
            // setMessage('Failed to delete template.');
            setNotification({
                message: 'Failed to Delete template. Please try again.',
                type: 'error',
            });
            setTimeout(() => setNotification(null), 4000);
        }
    };

    // Close the delete confirmation modal
    const handleDeleteConfirmClose = () => {
        setDeleteConfirmOpen(false);
    };

    return (
        <Box>
            <Container maxWidth="lg" sx={{ mt: 0 }}>
                {notification && (
                    <Notification
                        message={notification.message}
                        type={notification.type}
                        onClose={() => setNotification(null)}
                    />
                )}
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold', // Make the title bold
                        color: colors.primaryApplicationText, // Use theme color for consistency
                        textAlign: 'center', // Center align the title
                        mb: 2, // Add bottom margin
                    }}
                >
                    Template Management
                </Typography>

                {/* Toggle Button */}
                <Box sx={{ textAlign: 'right', mb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => setIsAddingTemplate(!isAddingTemplate)}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: colors.primaryBannerBlue,
                            '&:hover': { backgroundColor: colors.primaryApplicationText },
                        }}
                    >
                        {isAddingTemplate ? 'View Existing Templates' : 'Add Template'}
                    </Button>
                </Box>

                {/* Upload Template Section */}

                {isAddingTemplate ? (
                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: '#ffffff', // White card background for contrast
                            overflow: 'hidden',
                        }}
                    >
                        <CardContent
                            sx={{
                                padding: '24px',
                                backgroundColor: '#f4f6f8', // Light background to match theme
                            }}
                        >
                            <Typography
                                variant="h5"
                                gutterBottom
                                sx={{
                                    fontWeight: '600',
                                    color: colors.primaryApplicationText, // Match theme text color
                                    textAlign: 'center',
                                    mb: 3,
                                }}
                            >
                                Upload Survey Template
                            </Typography>
                            <form onSubmit={handleSubmit}>
                                {/* Template Name Field */}
                                <TextField
                                    label="Template Name"
                                    value={templateName}
                                    onChange={handleTemplateNameChange}
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        mb: 3,
                                        '& .MuiInputLabel-root': { color: colors.primaryApplicationText }, // Label text
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: colors.primaryBannerBlue, // Border matches primary color
                                            },
                                            '&:hover fieldset': {
                                                borderColor: colors.primaryApplicationText, // Darker hover border
                                            },
                                        },
                                    }}
                                />

                                {/* File Upload Field */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        sx={{
                                            borderColor: colors.primaryBannerBlue,
                                            color: colors.primaryBannerBlue,
                                            '&:hover': {
                                                borderColor: colors.primaryApplicationText,
                                                color: colors.primaryApplicationText,
                                                backgroundColor: '#e0f7fa', // Subtle hover effect
                                            },
                                            textTransform: 'none',
                                            mb: 1,
                                        }}
                                    >
                                        Choose File
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mt: 1 }}
                                    >
                                        Accepted formats: .xlsx
                                    </Typography>
                                    {/* Help Message */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mt: 2,
                                            backgroundColor: '#f0f4f8',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            width: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: '500',
                                                color: colors.primaryApplicationText,
                                                marginRight: '10px',
                                            }}
                                        >
                                            Need help? Download the sample template to get started.
                                        </Typography>
                                        <Tooltip title="Download Sample Template" arrow>
                                            <IconButton
                                                href="/International-Student-Survey-Final_v1.xlsx"
                                                download
                                                sx={{
                                                    backgroundColor: colors.primaryBannerBlue,
                                                    color: '#ffffff',
                                                    '&:hover': {
                                                        backgroundColor: colors.primaryApplicationText,
                                                    },
                                                    padding: '4px',
                                                }}
                                            >
                                                <DownloadIcon
                                                    sx={{
                                                        fontSize: '20px',
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Upload Button */}
                                <Button
                                    variant="contained"
                                    type="submit"
                                    fullWidth
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: '600',
                                        fontSize: '1rem',
                                        backgroundColor: colors.primaryBannerBlue,
                                        color: '#ffffff',
                                        '&:hover': { backgroundColor: colors.primaryApplicationText },
                                        transition: '0.3s',
                                    }}
                                >
                                    Upload Template
                                </Button>
                            </form>
                            {message && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 3,
                                        textAlign: 'center',
                                        color: colors.primaryApplicationText, // Match theme text
                                    }}
                                >
                                    {message}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Card elevation={2} sx={{ borderRadius: '12px', padding: '16px' }}>
                        <CardContent>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 'bold',
                                    marginBottom: '16px',
                                    color: colors.primaryApplicationText,
                                }}
                            >
                                Existing Templates
                            </Typography>

                            {templates.length > 0 ? (
                                <TableContainer>
                                    <Table>
                                        {/* Table Header */}
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', color: colors.primaryApplicationText }}>
                                                    Template
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: colors.primaryApplicationText,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        {/* Table Body */}
                                        <TableBody>
                                            {templates.map((template) => (
                                                <TableRow key={template.id} hover>
                                                    {/* Template Details */}
                                                    <TableCell>
                                                        <Typography
                                                            variant="h7"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                color: colors.primaryApplicationText,
                                                            }}
                                                        >
                                                            {template.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontStyle: 'italic',
                                                                color: colors.primaryApplicationText,
                                                                marginTop: '4px',
                                                            }}
                                                        >
                                                            ID: {template.id}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Actions */}
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <Tooltip title="View Template" arrow>
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={() => handleTemplateClick(template)}
                                                                    sx={{
                                                                        textTransform: 'none',
                                                                        fontSize: '0.875rem',
                                                                        borderColor: colors.primaryBannerBlue,
                                                                        color: colors.primaryBannerBlue,
                                                                        '&:hover': {
                                                                            borderColor: colors.primaryApplicationText,
                                                                            color: colors.primaryApplicationText,
                                                                            backgroundColor: '#f0f4f8',
                                                                        },
                                                                    }}
                                                                >
                                                                    View
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title="Create Survey from Template" arrow>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={() => handleCreateSurveyClick(template.id)}
                                                                    sx={{
                                                                        textTransform: 'none',
                                                                        fontSize: '0.875rem',
                                                                        backgroundColor: colors.primaryBannerBlue,
                                                                        color: '#ffffff',
                                                                        '&:hover': {
                                                                            backgroundColor: colors.primaryApplicationText,
                                                                        },
                                                                    }}
                                                                >
                                                                    Create Survey
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip title="Delete Template" arrow>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    onClick={() => handleDeleteTemplate(template)}
                                                                    sx={{
                                                                        textTransform: 'none',
                                                                        fontSize: '0.875rem',
                                                                        borderColor: '#f44336',
                                                                        color: '#f44336',
                                                                        '&:hover': {
                                                                            borderColor: '#d32f2f',
                                                                            color: '#d32f2f',
                                                                            backgroundColor: '#fdecea',
                                                                        },
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{
                                        textAlign: 'center',
                                        padding: '16px 0',
                                    }}
                                >
                                    No templates available.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Confirmation Dialog for Delete */}
                <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete the template <strong>{selectedTemplate?.title}</strong>?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteConfirmClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={confirmDeleteTemplate} color="secondary" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog to show the template details */}
                <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ backgroundColor: '#3f51b5', color: '#fff' }}>
                        Template Details
                    </DialogTitle>
                    <DialogContent>
                        {selectedTemplate && (
                            <>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    Sections
                                </Typography>
                                {selectedTemplate.sections.map((section, index) => (
                                    <Card key={index} sx={{ mb: 2, backgroundColor: '#f5f5f5' }}>
                                        <CardContent>
                                            <Typography variant="subtitle1" gutterBottom>
                                                {section.sectionTitle}
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            {section.questions.map((question, qIndex) => (
                                                <Box key={qIndex} sx={{ mb: 1 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                        {question.questionText}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Question Type: {question.type}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ))}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} sx={{ color: '#3f51b5', fontWeight: 'bold' }}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Dialog
                        open={createSurveyOpen}
                        onClose={handleCreateSurveyClose}
                        maxWidth="md"
                        fullWidth
                        sx={{
                            '& .MuiPaper-root': {
                                borderRadius: '16px',
                                padding: '10px',
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    >
                        <DialogTitle
                            sx={{
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: colors.primaryApplicationText,
                                paddingBottom: '10px',
                            }}
                        >
                            Create Survey
                        </DialogTitle>
                        <DialogContent dividers sx={{ paddingTop: 3, paddingBottom: 3 }}>
                            <Grid container spacing={3}>
                                {/* Survey Name */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Survey Name"
                                        name="surveyName"
                                        value={surveyData.surveyName}
                                        onChange={handleSurveyDataChange}
                                        fullWidth
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: colors.primaryBannerBlue,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primaryApplicationText,
                                                },
                                            },
                                            '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                        }}
                                    />
                                </Grid>

                                {/* Survey Description */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Survey Description"
                                        name="surveyDescription"
                                        value={surveyData.surveyDescription}
                                        onChange={handleSurveyDataChange}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: colors.primaryBannerBlue,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primaryApplicationText,
                                                },
                                            },
                                            '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                        }}
                                    />
                                </Grid>

                                {/* University Name */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="University Name"
                                        name="universityName"
                                        value={surveyData.universityName}
                                        onChange={handleSurveyDataChange}
                                        fullWidth
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: colors.primaryBannerBlue,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primaryApplicationText,
                                                },
                                            },
                                            '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                        }}
                                    />
                                </Grid>

                                {/* Logo Upload */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        type="file"
                                        inputProps={{ accept: 'image/*' }}
                                        onChange={handleLogoChange}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: colors.primaryBannerBlue,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primaryApplicationText,
                                                },
                                            },
                                            '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                        }}
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ marginTop: '4px', display: 'block' }}>
                                        Accepted formats: PNG, JPG
                                    </Typography>
                                </Grid>

                                {/* Number of Unique Links */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Number of Unique Links"
                                        name="uniqueLinksCount"
                                        value={surveyData.uniqueLinksCount}
                                        onChange={handleSurveyDataChange}
                                        fullWidth
                                        type="number"
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: colors.primaryBannerBlue,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primaryApplicationText,
                                                },
                                            },
                                            '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                        }}
                                    />
                                </Grid>

                                {/* Valid Till Date */}
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Valid Till"
                                        value={surveyData.validTill}
                                        onChange={handleValidTillChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: colors.primaryBannerBlue,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: colors.primaryApplicationText,
                                                        },
                                                    },
                                                    '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                padding: '16px 24px',
                                justifyContent: 'space-between',
                                borderTop: `1px solid ${colors.primaryBannerBlue}`,
                            }}
                        >
                            <Button
                                onClick={handleCreateSurveyClose}
                                variant="outlined"
                                sx={{
                                    textTransform: 'none',
                                    borderColor: colors.primaryBannerBlue,
                                    color: colors.primaryBannerBlue,
                                    '&:hover': {
                                        borderColor: colors.primaryApplicationText,
                                        color: colors.primaryApplicationText,
                                        backgroundColor: '#f0f4f8',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateSurvey}
                                variant="contained"
                                sx={{
                                    textTransform: 'none',
                                    backgroundColor: colors.primaryBannerBlue,
                                    color: '#ffffff',
                                    '&:hover': {
                                        backgroundColor: colors.primaryApplicationText,
                                    },
                                }}
                            >
                                Create Survey
                            </Button>
                        </DialogActions>
                    </Dialog>
                </LocalizationProvider>


            </Container>
        </Box>
    );
};

export default TemplateManagement;
